import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  DocumentThemeValues,
  InvoicingBankAccount,
  LoyaltyPointRounding,
} from '@efacture/core';
import { apiClient } from '@/api/client';
import { authKeys } from './auth';

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────

/** The running SFE build's attestation ISF (GET /settings/tenant, GET /establishments/sfe-isf). */
export interface SfeIsf {
  /** The running build is attested: the ISF entries are read-only. */
  attested: boolean;
  /** Its attestation's ISF; null before the attestation or when it records none. */
  isf: string | null;
}

export interface TenantSettings {
  id: string;
  name: string;
  slug: string;
  ifu: string;
  /** A `LEGAL_NATURES` code (@efacture/core), CGI art. 562, 5°. */
  legalNature: string | null;
  /** Legal form of a legal person, CGI art. 562, 5°. */
  legalForm: string | null;
  /** Register number; an association's declaration receipt. */
  rccm: string | null;
  /** « N° Empl. » of the CNSS annual statements (form Pr-RCS/Cot 06). */
  cnssEmployerNumber: string | null;
  address: string;
  /** Références cadastrales du lieu de vente, format SSSS LLL PPPP (§3.c). */
  cadastralReference: string | null;
  /** Postal box, CGI art. 562, 5°. */
  postalBox: string | null;
  phone: string | null;
  email: string | null;
  businessType: string;
  sectorPacks: string[];

  // MCF Configuration
  /** NIM prefix of the default establishment, which the MCF reads. */
  nimPrefix: string | null;
  /** NIM of the default establishment's virtual MCF, once created: the prefix is then fixed. */
  virtualMcfNim?: string | null;
  mcfPort: string | null;
  /** True when the API runs next to the MCF: the serial port and its detection work. */
  mcfOnSite: boolean;
  isf: string | null;
  /**
   * Whether the running SFE build is attested, and its attestation's ISF:
   * once it is, the company's ISF is read-only and that one is shown.
   * Absent from an API that does not send it, read as not attested.
   */
  sfeIsf?: SfeIsf;

  // Invoice Settings
  defaultPriceMode: 'HT' | 'TTC';
  currentFiscalYear: number;
  psvbApplicable: boolean;
  /** Rule C (#929): controlled reissue of an invoice's original, off by default. */
  originalReissueEnabled: boolean;
  /** Reissues allowed per invoice, 0 to 5. */
  originalReissueMax: number;
  /** Days after the first original at which it counts as released; null: never. */
  originalReleaseAfterDays: number | null;

  // Loyalty Settings
  loyaltyEnabled: boolean;
  loyaltyPointsPerFCFA: number;
  /** Francs one point settles; a Prisma Decimal, serialized as a string. */
  loyaltyPointValue?: number | string;
  loyaltyPointRounding?: LoyaltyPointRounding;

  // DGI Configuration
  taxRegime: string | null;
  taxOffice: string | null;
  /** Invoicing bank accounts (§2.32), in the current shape. */
  bankReferences: InvoicingBankAccount[];

  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateTenantSettingsInput {
  // Company Information
  name?: string;
  legalNature?: string | null;
  legalForm?: string | null;
  rccm?: string | null;
  cnssEmployerNumber?: string | null;
  address?: string;
  /** Références cadastrales du lieu de vente, format SSSS LLL PPPP (§3.c). */
  cadastralReference?: string | null;
  postalBox?: string | null;
  phone?: string | null;
  email?: string | null;
  businessType?: string;
  sectorPacks?: string[];

  // MCF Configuration
  nimPrefix?: string | null;
  mcfPort?: string | null;

  // Invoice Settings
  defaultPriceMode?: 'HT' | 'TTC';
  currentFiscalYear?: number;
  originalReissueEnabled?: boolean;
  originalReissueMax?: number;
  originalReleaseAfterDays?: number | null;

  // Loyalty Settings
  loyaltyEnabled?: boolean;
  loyaltyPointsPerFCFA?: number;
  loyaltyPointValue?: number;
  loyaltyPointRounding?: LoyaltyPointRounding;

  // DGI Configuration
  taxRegime?: string | null;
  taxOffice?: string | null;
  /** Identifiant du Système de Facturation, attribué par la DGI (§2.3). */
  isf?: string | null;
  /** The whole invoicing bank account list (§2.32); the API refuses one that prints none. */
  bankReferences?: InvoicingBankAccount[];
}

/** Whether invoices print a bank reference (§3 j). */
export interface BankReferencesStatus {
  configured: boolean;
}

/** Outcome of the C1h probe on one serial port (POST /settings/mcf/detect). */
export type McfPortProbeOutcome =
  | 'mcf'
  | 'no-response'
  | 'invalid-response'
  | 'nak'
  | 'busy'
  | 'access-denied'
  | 'not-found'
  | 'held-by-sfe'
  | 'not-probed'
  | 'error';

export interface McfPortProbeResult {
  path: string;
  configured: boolean;
  outcome: McfPortProbeOutcome;
  /** French, displayed as is. */
  message: string;
  vendorId: string | null;
  productId: string | null;
  manufacturer: string | null;
  mcf: {
    info: { machineId: string; nim: string | null; activationCount: string | null; ifu: string; dateTime: string };
    status: { hex: string; flags: Array<{ byte: number; bit: number; label: string }> };
  } | null;
  sentHex: string | null;
  receivedHex: string | null;
  durationMs: number;
}

export interface McfDiscoveryReport {
  platform: string;
  startedAt: string;
  durationMs: number;
  configuredPort: string | null;
  results: McfPortProbeResult[];
  excluded: Array<{ path: string; reason: string }>;
  found: McfPortProbeResult[];
  /** French explanations shown above the results. */
  notices: string[];
}

// ─────────────────────────────────────────────────────────────
// Query Keys
// ─────────────────────────────────────────────────────────────

export const settingsKeys = {
  all: ['settings'] as const,
  tenant: () => [...settingsKeys.all, 'tenant'] as const,
  documentTheme: () => [...settingsKeys.all, 'document-theme'] as const,
  documentLogo: (logoId: string) => [...settingsKeys.all, 'document-theme', 'logo', logoId] as const,
  bankReferencesStatus: () => [...settingsKeys.all, 'bank-references', 'status'] as const,
  ifuCorrection: () => [...settingsKeys.all, 'tenant', 'ifu'] as const,
};

/** Formats and size the API accepts for the company logo (PUT /settings/document-theme/logo). */
export const LOGO_ACCEPTED_TYPES = ['image/png', 'image/jpeg'] as const;
export const LOGO_MAX_BYTES = 500_000;

// ─────────────────────────────────────────────────────────────
// API Functions
// ─────────────────────────────────────────────────────────────

async function fetchTenantSettings(): Promise<TenantSettings> {
  return apiClient.get<TenantSettings>('/settings/tenant');
}

async function updateTenantSettings(input: UpdateTenantSettingsInput): Promise<TenantSettings> {
  return apiClient.put<TenantSettings>('/settings/tenant', input);
}

/** One version of the document theme (#536), as GET /settings/document-theme returns it. */
export interface DocumentThemeVersion extends DocumentThemeValues {
  version: number;
  /** `refusal`: why the stored logo is no longer accepted (it then prints nowhere), or null. */
  logo: { id: string; mimeType: string; sha256: string; refusal: string | null } | null;
  createdAt: string;
}

export interface DocumentThemeState {
  /** The latest version, null while the company never saved one. */
  theme: DocumentThemeVersion | null;
  defaults: DocumentThemeValues;
}

export interface SaveDocumentThemeInput extends DocumentThemeValues {
  /** Saves a version without a logo. */
  removeLogo?: boolean;
}

async function uploadDocumentLogo(file: File): Promise<{ theme: DocumentThemeVersion }> {
  const form = new FormData();
  form.append('file', file);
  return apiClient.putForm<{ theme: DocumentThemeVersion }>('/settings/document-theme/logo', form);
}

async function detectMcfPorts(): Promise<McfDiscoveryReport> {
  const { detection } = await apiClient.post<{ detection: McfDiscoveryReport }>(
    '/settings/mcf/detect',
    undefined,
    { noQueue: true }
  );
  return detection;
}

// ─────────────────────────────────────────────────────────────
// Queries
// ─────────────────────────────────────────────────────────────

/**
 * Hook to fetch tenant settings
 * GET /api/settings/tenant
 */
export function useTenantSettings(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: settingsKeys.tenant(),
    queryFn: fetchTenantSettings,
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: options?.enabled ?? true,
  });
}

/**
 * Whether invoices print a bank reference (§3 j), for any signed-in user.
 * GET /api/settings/bank-references/status
 */
export function useBankReferencesStatus() {
  return useQuery({
    queryKey: settingsKeys.bankReferencesStatus(),
    queryFn: () => apiClient.get<BankReferencesStatus>('/settings/bank-references/status'),
    staleTime: 1000 * 60 * 5,
  });
}

/**
 * The latest document theme version and the default values.
 * GET /api/settings/document-theme
 */
export function useDocumentTheme() {
  return useQuery({
    queryKey: settingsKeys.documentTheme(),
    queryFn: () => apiClient.get<DocumentThemeState>('/settings/document-theme'),
    staleTime: 1000 * 60 * 5,
  });
}

/**
 * The latest version's logo as a Blob, for the preview; keyed by the logo's
 * id, so a new logo is fetched once.
 * GET /api/settings/document-theme/logo
 */
export function useDocumentLogo(logoId: string | null) {
  return useQuery({
    queryKey: settingsKeys.documentLogo(logoId ?? 'none'),
    queryFn: ({ signal }) => apiClient.blob('/settings/document-theme/logo', signal),
    enabled: logoId !== null,
    staleTime: Infinity,
  });
}

// ─────────────────────────────────────────────────────────────
// Mutations
// ─────────────────────────────────────────────────────────────

function storeDocumentTheme(queryClient: ReturnType<typeof useQueryClient>, theme: DocumentThemeVersion) {
  queryClient.setQueryData<DocumentThemeState>(settingsKeys.documentTheme(), (current) =>
    current ? { ...current, theme } : current
  );
  queryClient.invalidateQueries({ queryKey: settingsKeys.documentTheme() });
}

/**
 * Saves the colours, logo placement and size as a new theme version.
 * PUT /api/settings/document-theme
 */
export function useSaveDocumentTheme() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SaveDocumentThemeInput) =>
      apiClient.put<{ theme: DocumentThemeVersion }>('/settings/document-theme', input),
    onSuccess: ({ theme }) => storeDocumentTheme(queryClient, theme),
  });
}

/**
 * Uploads the company logo; the API saves a theme version that prints it.
 * PUT /api/settings/document-theme/logo (multipart)
 */
export function useUploadDocumentLogo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: uploadDocumentLogo,
    onSuccess: ({ theme }) => storeDocumentTheme(queryClient, theme),
  });
}

/** Whether the company's IFU can still be corrected, and why not. */
export interface IfuCorrectionState {
  allowed: boolean;
  reason: string | null;
}

/**
 * GET /api/settings/tenant/ifu
 */
export function useTenantIfuCorrection() {
  return useQuery({
    queryKey: settingsKeys.ifuCorrection(),
    queryFn: () => apiClient.get<IfuCorrectionState>('/settings/tenant/ifu'),
  });
}

/**
 * Corrects the company's IFU, with the reason kept in the audit log.
 * POST /api/settings/tenant/ifu (409 once an invoice is issued or an MCF bound)
 */
export function useCorrectTenantIfu() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { ifu: string; reason: string }) =>
      apiClient.post<{ ifu: string }>('/settings/tenant/ifu', input, { noQueue: true }),
    onSuccess: ({ ifu }) => {
      queryClient.setQueryData<TenantSettings>(settingsKeys.tenant(), (current) =>
        current ? { ...current, ifu } : current
      );
      queryClient.invalidateQueries({ queryKey: settingsKeys.ifuCorrection() });
      queryClient.invalidateQueries({ queryKey: authKeys.currentUser() });
    },
  });
}

/**
 * Probes the serial ports of the on-site machine for the MCF.
 * POST /api/settings/mcf/detect (409 on a cloud install)
 */
export function useDetectMcfPorts() {
  return useMutation({ mutationFn: detectMcfPorts });
}

/**
 * Hook to update tenant settings
 * PUT /api/settings/tenant
 */
export function useUpdateTenantSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateTenantSettingsInput) => updateTenantSettings(input),
    onSuccess: (updatedSettings) => {
      // Update the tenant settings in cache
      queryClient.setQueryData(settingsKeys.tenant(), updatedSettings);
      queryClient.invalidateQueries({ queryKey: settingsKeys.bankReferencesStatus() });
      // Invalidate auth cache since tenant name might have changed
      queryClient.invalidateQueries({ queryKey: authKeys.currentUser() });
    },
  });
}

// ─────────────────────────────────────────────────────────────
// Utility Exports
// ─────────────────────────────────────────────────────────────

export {
  fetchTenantSettings,
  updateTenantSettings,
};
